import { AppState, UserRole } from "../../../@types";
import { Location } from "history";
import {
  hasGlobalSession,
  getEnvironment,
  getRouteName,
} from "../../../helpers";
import { getLanguageFromLocationOrState } from "../../../helpers/languageHelpers";
import { isBeneNewToMedicare } from "../../../helpers/objectUtilities";
import { getYearPartInfo } from "../../../helpers/yearPartHelpers";
import routes from "../../routes";
import { Ga4EventDimensionsConfig, ga4EventDimensionValues } from "./constants";
import {
  Ga4EventDimensions,
  Ga4EventDimension,
  Ga4PageViewDimensions,
  TealiumPageViewProps,
  TealiumEventProps,
  UtagPageViewProps,
} from "./types";
import { LDFlagSet } from "launchdarkly-js-client-sdk";

/**
 * Constructs and sends the proper GA4 object depending on state, analytics settings passed in, and typing in Ga4EventDimensionsConfig
 * @param settings
 * @param state
 */
export function createGa4Event({
  settings,
  state,
}: {
  settings: Ga4EventDimensions;
  state: AppState;
}): void {
  const { event_name } = settings;
  const {
    beneficiary,
    csr,
    cwCoverage,
    language,
    lis,
    planType,
    prescriptions,
    selectedPlan,
    viewMedigap,
    cachedFlags: flags,
  } = state;
  const { isOutsideOpenEnrollment } = getYearPartInfo(flags);

  const logged_in = hasGlobalSession(state);
  const open_enrollment = !isOutsideOpenEnrollment;

  const dimensionValueMapping = {
    [Ga4EventDimension.CONTENT_LANGUAGE]: language,
    [Ga4EventDimension.LOGGED_IN]: logged_in,
    [Ga4EventDimension.MCT_COVERAGE_WIZARD]: !!cwCoverage,
    [Ga4EventDimension.MCT_INSURANCE_PROVIDER]:
      settings.mct_insurance_provider || selectedPlan?.name,
    [Ga4EventDimension.MCT_INSURANCE_TYPE]:
      settings.mct_insurance_type || selectedPlan?.plan_type,
    [Ga4EventDimension.MCT_LIS]: lis,
    [Ga4EventDimension.MCT_MEDIGAP]:
      settings.mct_medigap !== undefined ? settings.mct_medigap : viewMedigap,
    [Ga4EventDimension.MCT_PHARMACY_TYPE]:
      settings.mct_pharmacy_type !== undefined
        ? settings.mct_pharmacy_type
        : state.pharmacyType,
    [Ga4EventDimension.MCT_PLAN_TYPE]: settings.mct_plan_type || planType,
    [Ga4EventDimension.MCT_VIEWED_DRUGS]: prescriptions.length > 0,
    [Ga4EventDimension.OPEN_ENROLLMENT]: open_enrollment,
    [Ga4EventDimension.ROLE]: (() => {
      let role = UserRole.ANONYMOUS;
      if (csr) {
        role = UserRole.CSR;
      } else if (beneficiary) {
        role = UserRole.BENEFICIARY;
      }
      return role;
    })(),
    [Ga4EventDimension.SITE_ENVIRONMENT]: getEnvironment(),
    [Ga4EventDimension.SITE_DOMAIN]: window.location.hostname,
    [Ga4EventDimension.CSR_ID]: beneficiary?.csr_id,
    [Ga4EventDimension.BENEFICIARY_KEY]: beneficiary?.meta_data.beneficiary_key,
  };
  const eventDimensions = Ga4EventDimensionsConfig[event_name] || [];

  const dimensions = { event_name };

  eventDimensions.forEach(k => {
    const mappedValue = dimensionValueMapping[k];
    const settingsValue = settings[k];
    if (mappedValue !== undefined) {
      dimensions[k] = mappedValue;
    } else if (settingsValue !== undefined) {
      if (typeof settingsValue === "object" && !Array.isArray(settingsValue)) {
        Object.entries(settingsValue).forEach(([key, value]) => {
          if (
            ga4EventDimensionValues.find(dimension => dimension === key) &&
            typeof value !== "undefined"
          ) {
            dimensions[key] = value;
          }
        });
      } else {
        dimensions[k] = settingsValue;
      }
    }
  });
  window.utag?.link(dimensions);
}

export function sendTealiumEvent({
  event_action,
  event_label,
  other_props = {},
}: TealiumEventProps): void {
  if (window.utag) {
    const event_category = "MCT";

    window.utag.link({
      event_category,
      event_action,
      event_label,
      event_noninteraction: 0,
      ...other_props,
    });
  }
}

/**
 * Used on CSR Landing page only, otherwise use sendTealiumEvent
 */
export function sendTealiumLink(props: Record<string, unknown>): void {
  if (window.utag) {
    window.utag.link(props);
  }
}

/**
 * Constructs the proper GA4 page-view object depending on state, router location, and flags
 * @param state
 * @param location
 * @returns - GA4 page-view object
 */
export function createGa4PageViewPayload({
  state,
  location: routerLocation,
  flags,
}: {
  state: AppState;
  location: Location;
  flags: LDFlagSet | undefined;
}): Ga4PageViewDimensions {
  const { beneficiary, csr, language: stateLang } = state;
  const language = getLanguageFromLocationOrState({
    location: routerLocation,
    stateLang,
  });
  const { isOutsideOpenEnrollment } = getYearPartInfo(flags);
  const site_domain = window.location.host;
  const site_environment = getEnvironment();
  const site_section = "mct";
  const content_type = "mct page";
  const content_language = language;
  const logged_in = hasGlobalSession(state) ? "true" : "false";
  const page_name = getRouteName(routerLocation.pathname);
  const open_enrollment = !isOutsideOpenEnrollment ? "true" : "false";

  let role = UserRole.ANONYMOUS;

  if (csr) {
    role = UserRole.CSR;
  } else if (beneficiary) {
    role = UserRole.BENEFICIARY;
  }

  return {
    content_language,
    content_type,
    logged_in,
    open_enrollment,
    page_name,
    role,
    site_domain,
    site_environment,
    site_section,
  };
}

/**
 * Constructs the proper tealium page-view object depending on state and TealiumPageViewProps
 */
export function createTealiumPageViewPayload({
  props: { mctCurrentPlanYear, page_name, page_type = "application" },
  state: appState,
}: {
  props: TealiumPageViewProps;
  state: AppState;
}): Record<string, unknown> {
  const page = `${window.location.pathname}${window.location.hash.replace(
    /\?.+/,
    ""
  )}`;
  let lis_level = appState.lis || "";
  if (
    appState.futureLis &&
    mctCurrentPlanYear &&
    appState.year !== mctCurrentPlanYear.toString()
  ) {
    lis_level = appState.futureLis;
  }

  return {
    page,
    page_name,
    page_type,
    count_insurance_carriers: appState.searchResultsFilters.insuranceCarriers.length.toString(),
    count_pharmacies: appState.pharmacies.length.toString(),
    count_prescriptions: appState.prescriptions.length.toString(),
    insurance_carriers: appState.searchResultsFilters.insuranceCarriers.join(
      "|"
    ),
    lis_level,
    language: window.location.hash.includes("lang=es")
      ? "es"
      : appState.language,
    pharmacy_type: appState.pharmacyType,
    plan_type: appState.planType || "",
    prescriptions_ndc: appState.prescriptions.map(p => p.ndc).join("|"),
    logged_in: appState.beneficiary ? "true" : "false",
    user_type: appState.csr
      ? "csr"
      : appState.beneficiary
      ? "beneficiary"
      : "guest",
    view_coverage_wizard: appState.cwCoverage ? "true" : "false",
    new_to_medicare_beneficiary: isBeneNewToMedicare(appState),
  };
}

export const shouldSendTealiumView = () => {
  const page = `${window.location.pathname}${window.location.hash.replace(
    /\?.+/,
    ""
  )}`;
  return (
    !page.includes(routes.slsCallback) && !page.includes(routes.mbpLandingPage)
  );
};

/**
 * Sends a virtual page view for UA, only
 */
export function sendTealiumView({
  props: { page_name, page_type = "application", mctCurrentPlanYear },
  state,
}: {
  props: TealiumPageViewProps;
  state: AppState;
}): void {
  if (!window.utag?.view || !shouldSendTealiumView()) {
    return;
  }

  window.utag.view(
    createTealiumPageViewPayload({
      props: {
        page_name,
        mctCurrentPlanYear,
        page_type,
      },
      state,
    })
  );
}

/**
 * Creates a virtual page view for GA4 and, if appropriate for the current
 * route/location, UA. Called from the `ReferrerDetector` component to handle
 * triggering a view on every route change
 */
export function createUTagPageView({
  props: {
    location: routerLocation,
    mctCurrentPlanYear,
    page_name,
    page_type = "application",
  },
  state,
}: {
  props: UtagPageViewProps;
  state: AppState;
}): void {
  if (!window.utag?.view) {
    return;
  }
  let payload:
    | Ga4PageViewDimensions
    | Record<string, unknown> = createGa4PageViewPayload({
    state,
    location: routerLocation,
    flags: state.cachedFlags,
  });
  if (shouldSendTealiumView()) {
    payload = {
      ...createTealiumPageViewPayload({
        props: {
          page_name,
          mctCurrentPlanYear,
          page_type,
        },
        state,
      }),
      ...payload,
    };
  }
  window.utag.view(payload);
}
